import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Space, Table, Tag, Upload, message, Form, Row, Col, Card } from 'antd';
import { UploadOutlined, DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined, FileTextOutlined, HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageContentHeader from '../../../components/common/PageContentHeader';
import Title from 'antd/es/skeleton/Title';
import InputSelect from '../../../components/common/InputSelect';

const dummyUsedCars = Array.from({ length: 30 }).map((_, i) => ({
  key: i + 1,
  vin: `UC${String(i + 1).padStart(4, '0')}`,
  model: ['Glanza', 'Innova Hycross', 'Fortuner'][i % 3],
  variant: ['G', 'V', 'ZX'][i % 3],
  color: ['White', 'Grey', 'Red'][i % 3],
  year: 2020 + (i % 4),
  price: 500000 + (i * 50000),
  location: ['Bilaspur', 'Korba', 'Raipur'][i % 3],
  ownerName: ['Ravi Kumar', 'Anita Singh', 'Kiran Patel'][i % 3],
  status: ['Available', 'Sold'][i % 2],
  remarks: i % 5 === 0 ? 'Good condition' : '',
}));

const UsedCarsList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(dummyUsedCars);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState();
  const [model, setModel] = useState();
  const [location, setLocation] = useState();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(0); // 0: select file, 1: uploading, 2: ready to update

  const filtered = useMemo(() => {
    return data.filter((d) => {
      const matchesSearch = [d.vin, d.model, d.variant, d.color, d.ownerName]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesStatus = status ? d.status === status : true;
      const matchesModel = model ? d.model === model : true;
      const matchesLocation = location ? d.location === location : true;
      return matchesSearch && matchesStatus && matchesModel && matchesLocation;
    });
  }, [data, search, status, model, location]);

  const columns = [
    { title: 'VIN', dataIndex: 'vin', key: 'vin' },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Variant', dataIndex: 'variant', key: 'variant' },
    { title: 'Color', dataIndex: 'color', key: 'color' },
    { title: 'Year', dataIndex: 'year', key: 'year' },
    { title: 'Price', dataIndex: 'price', key: 'price', render: (val) => `₹${val.toLocaleString()}` },
    { title: 'Location', dataIndex: 'location', key: 'location' },
    { title: 'Owner', dataIndex: 'ownerName', key: 'ownerName' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val) => <Tag color={val === 'Available' ? 'green' : 'red'}>{val}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size='small' onClick={() => navigate(`/admin/stock/edit-used-car/${row.vin}`)}>Edit</Button>
          <Button size='small' danger onClick={() => handleDelete([row.key])}>Delete</Button>
        </Space>
      ),
    },
  ];

  const handleDelete = (keys) => {
    Modal.confirm({
      title: 'Delete selected vehicle(s)?',
      icon: <ExclamationCircleOutlined />,
      content: 'This is a UI-only mock. Selected rows will be removed locally.',
      onOk: () => {
        setData((prev) => prev.filter((r) => !keys.includes(r.key)));
        setSelectedRowKeys([]);
        message.success('Deleted locally');
      },
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const handleFileUpload = (file) => {
    setUploadFile(file);
    setUploadStep(1);
    setIsUploading(true);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          setUploadStep(2);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
    
    return false; // Prevent default upload
  };

  const handleCancelUpload = () => {
    setUploadFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadStep(0);
  };

  const handleUpdate = () => {
    setUploadStep(0);
    setIsUploadOpen(false);
    setIsResultOpen(true);
    setUploadFile(null);
    setUploadProgress(0);
  };

  const handleCloseUpload = () => {
    setUploadStep(0);
    setIsUploadOpen(false);
    setUploadFile(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  return (
    <div className='user-data'>
      <PageContentHeader title='Stock - Used Cars' />
      <div className='personal-detail'>
        <div className='user-left d-flex align-items-center'>
          <Title level={4} className='pe-2 pb-2'>Stock - Used Cars</Title>
        </div>
        <Card>
          <Form layout='vertical'>
            {/* Search Bar - Full Width */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={16}>
                <Input.Search 
                  className='form-control-ant' 
                  allowClear 
                  placeholder='Search VIN/Model/Color/Owner' 
                  onChange={(e) => setSearch(e.target.value)} 
                  size='large'
                />
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setStatus(undefined); setModel(undefined); setLocation(undefined); }}>
                    Reset
                  </Button>
                  <Button type='primary' style={{ backgroundColor: '#0a9551', borderColor: '#0a9551' }} onClick={() => message.success('Filters applied (mock)')}>
                    Apply
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Filters Row */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <InputSelect
                  name='sort_by'
                  placeholder='Sort By'
                  className='form-control-ant'
                  allowClear
                  options={[
                    { id: 'status', name: 'Status' },
                    { id: 'model', name: 'Model' },
                    { id: 'location', name: 'Location' },
                    { id: 'year', name: 'Year' },
                  ]}
                  optionName='name'
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <InputSelect
                  name='status'
                  placeholder='Status'
                  className='form-control-ant'
                  allowClear
                  options={[
                    { id: 'Available', name: 'Available' },
                    { id: 'Sold', name: 'Sold' },
                  ]}
                  optionName='name'
                  onSelect={(val) => setStatus(val)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <InputSelect
                  name='model'
                  placeholder='Model'
                  className='form-control-ant'
                  allowClear
                  options={[
                    { id: 'Glanza', name: 'Glanza' },
                    { id: 'Innova Hycross', name: 'Innova Hycross' },
                    { id: 'Fortuner', name: 'Fortuner' },
                  ]}
                  optionName='name'
                  onSelect={(val) => setModel(val)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <InputSelect
                  name='location'
                  placeholder='Location'
                  className='form-control-ant'
                  allowClear
                  options={[
                    { id: 'Bilaspur', name: 'Bilaspur' },
                    { id: 'Korba', name: 'Korba' },
                    { id: 'Raipur', name: 'Raipur' },
                  ]}
                  optionName='name'
                  onSelect={(val) => setLocation(val)}
                />
              </Col>
            </Row>

            {/* Action Buttons Row */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                  <Button icon={<FileTextOutlined />} style={{ backgroundColor: '#0a9551', borderColor: '#0a9551', color: '#fff' }} onClick={() => message.info('Downloaded latest template (mock)')}>
                    Template
                  </Button>
                  <Button icon={<UploadOutlined />} style={{ backgroundColor: '#f0ad4e', borderColor: '#f0ad4e', color: '#fff' }} onClick={() => setIsUploadOpen(true)}>
                    Bulk Update
                  </Button>
                  <Button danger icon={<DeleteOutlined />} onClick={() => selectedRowKeys.length ? handleDelete(selectedRowKeys) : message.warning('Select rows')}>
                    Delete
                  </Button>
                  <Button icon={<HistoryOutlined />} type='primary' style={{ backgroundColor: '#0a9551', borderColor: '#0a9551' }} onClick={() => navigate('/admin/stock/history')}>
                    Update History
                  </Button>
                  <Button type='primary' onClick={() => navigate('/admin/stock/add-used-car')}>
                    Add Used Car
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
          <Table size='middle' rowSelection={rowSelection} columns={columns} dataSource={filtered} pagination={{ pageSize: 10 }} />
        </Card>
      </div>

      {/* Upload Modal - Step 1: File Selection */}
      <Modal 
        open={isUploadOpen && uploadStep === 0} 
        onCancel={handleCloseUpload}
        footer={null}
        title='Bulk Update - Upload File (.xls, .xlsx)'
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Upload.Dragger 
            beforeUpload={handleFileUpload} 
            multiple={false} 
            accept='.xls,.xlsx'
            showUploadList={false}
          >
            <p className='ant-upload-drag-icon' style={{ fontSize: '48px', color: '#1890ff' }}>
              <UploadOutlined />
            </p>
            <p className='ant-upload-text' style={{ fontSize: '16px', marginBottom: '8px' }}>
              Click or drag file to this area to upload
            </p>
            <p className='ant-upload-hint' style={{ color: '#666' }}>
              Support for .xls and .xlsx files only
            </p>
          </Upload.Dragger>
        </div>
      </Modal>

      {/* Upload Modal - Step 2: Upload Progress */}
      <Modal 
        open={isUploadOpen && uploadStep === 1} 
        onCancel={isUploading ? null : handleCancelUpload}
        footer={[
          <Button key='cancel' onClick={handleCancelUpload} disabled={isUploading}>
            Cancel
          </Button>
        ]}
        title='Bulk Update - Uploading File'
        width={500}
        closable={!isUploading}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '16px' }}>
            <strong>File Name:</strong> {uploadFile?.name}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <strong>File Size:</strong> {uploadFile ? (uploadFile.size / 1024 / 1024).toFixed(2) + ' MB' : '0 MB'}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>Upload Progress:</strong>
          </div>
          <div style={{ 
            width: '100%', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div 
              style={{ 
                width: `${uploadProgress}%`, 
                height: '20px', 
                backgroundColor: '#1890ff',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '8px', color: '#666' }}>
            {uploadProgress}%
          </div>
        </div>
      </Modal>

      {/* Upload Modal - Step 3: Ready to Update */}
      <Modal 
        open={isUploadOpen && uploadStep === 2} 
        onCancel={handleCloseUpload}
        footer={[
          <Button key='cancel' onClick={handleCloseUpload}>
            Cancel
          </Button>,
          <Button key='update' type='primary' onClick={handleUpdate}>
            Update
          </Button>
        ]}
        title='Bulk Update - File Ready'
        width={500}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}>
            ✓
          </div>
          <div style={{ marginBottom: '16px' }}>
            <strong>File Name:</strong> {uploadFile?.name}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <strong>File Size:</strong> {uploadFile ? (uploadFile.size / 1024 / 1024).toFixed(2) + ' MB' : '0 MB'}
          </div>
          <div style={{ color: '#52c41a', fontSize: '16px' }}>
            File uploaded successfully! Click Update to process the data.
          </div>
        </div>
      </Modal>

      {/* Result Modal (Mock) */}
      <Modal open={isResultOpen} onCancel={() => setIsResultOpen(false)} footer={[
        <Button key='download' onClick={() => message.success('Result summary downloaded (mock)')}>Download Result</Button>,
        <Button key='close' type='primary' onClick={() => setIsResultOpen(false)}>Close</Button>,
      ]} title='Upload Summary'>
        <p>Total Rows: 50</p>
        <p>Added: 30</p>
        <p>Updated: 15</p>
        <p>Failed: 5</p>
      </Modal>
    </div>
  );
};

export default UsedCarsList;